import React, { useMemo, useState, useEffect } from 'react'
import { css } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/app/hooks'
import { Card } from '@/components/atoms/card'
import { CopyIcon, OpenTabIcon } from '@/components/atoms/icon'
import { CUSTOM_ICON } from '@/components/template/custom-design-style'

type Props = {
  comment: string // コピーするフィードバックコメント
  googleMapUrl: string // Google MapsのURL
}

/**
 * Google Mapsレビュー用のフィードバックコピー＆リンクコンポーネント
 *
 * 機能：
 * - フィードバックコメントを表示
 * - コメントをクリップボードにコピー
 * - Google Mapsを新しいタブで開く
 * - コピー成功時に「Copied!」ポップアップを表示
 */
export const GoogleMapCopyLink = ({ comment, googleMapUrl }: Props) => {
  const { t } = useTranslation()
  const design = useAppSelector(state => state.design)
  // コピー成功状態を管理（3秒後に自動でfalseに戻る）
  const [isCopied, setIsCopied] = useState(false)

  // アイコンの色をデザインテーマから取得
  const iconColor = design?.form.textColor

  // ボタンスタイル：デザインテーマのブランドカラーを使用
  const googleMapCopyLinkButtonStyle = useMemo(
    () =>
      css({
        backgroundColor: design?.theme.brandColor,
        color: design?.background.cardColor,
        padding: '1rem',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
        position: 'relative', // コピー成功ポップアップの配置用
        [`& svg.${CUSTOM_ICON}`]: {
          '& :not([fill="none"])': {
            fill: iconColor,
          },
          '& :not([stroke="none"]) [stroke]': {
            stroke: iconColor,
          },
        },
        [`& .${CUSTOM_ICON}`]: {
          '& svg :not([fill="none"])': {
            fill: iconColor,
          },
          '& svg :not([stroke="none"]) [stroke]': {
            stroke: iconColor,
          },
        },
      }),
    [design, iconColor],
  )

  // isCopiedがtrueになったら、少し待ってからリンクを開く
  useEffect(() => {
    if (isCopied) {
      const openTimer = setTimeout(() => {
        window.open(googleMapUrl, '_blank', 'noopener,noreferrer')
      }, 300)

      const hideTimer = setTimeout(() => {
        setIsCopied(false)
      }, 1000)

      return () => {
        clearTimeout(openTimer)
        clearTimeout(hideTimer)
      }
    }
  }, [isCopied, googleMapUrl])

  const handleCopy = async () => {
    // 1. クリップボードにコピー
    await navigator.clipboard.writeText(comment)

    // 2. コピー成功モーダルを表示（useEffectでリンクを開く）
    setIsCopied(true)
  }

  return (
    <Card cardCss={googleMapCopyLinkContainerStyle}>
      {/* フィードバックタイトル */}
      <p css={googleMapCopyLinkTitleStyle}>{t('Your Feedback')}</p>
      {/* フィードバックコメント */}
      <p css={googleMapCopyLinkCommentStyle}>{comment}</p>
      {/* コピー＆リンクボタン */}
      <div css={googleMapCopyLinkButtonStyle} onClick={() => handleCopy()}>
        {/* コピーボタン（左側） */}
        <p css={buttonTextStyle}>
          <span>
            <CopyIcon style={{ fill: iconColor }} />
          </span>
          {t('Copy your feedback and write a review on Google Maps')}
        </p>
        {/* Google Mapsを開くボタン（右側） */}
        <div css={buttonIconStyle}>
          <OpenTabIcon style={{ fill: iconColor }} />
        </div>
        {/* コピー成功時に表示されるポップアップ */}
        {isCopied && (
          <div css={[copiedPopupStyle, css({ backgroundColor: design?.form.textColor, color: design?.background.backgroundColor })]}>
            {t('Copied!')}
          </div>
        )}
      </div>
    </Card>
  )
}

const googleMapCopyLinkContainerStyle = css({
  padding: '1.5rem',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  marginTop: '2rem',
  width: '100%',
})

const googleMapCopyLinkTitleStyle = css({
  fontSize: '14px',
  fontWeight: 'bold',
  fontFamily: 'Arial, Helvetica, sans-serif',
  margin: 0,
})

const googleMapCopyLinkCommentStyle = css({
  fontSize: '14px',
  margin: 0,
  wordBreak: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  lineHeight: '1.6',
})

const buttonTextStyle = css({
  margin: 0,
  fontSize: '14px',
  fontWeight: '500',
  lineHeight: '1.4',
  flex: 1,
  display: 'flex',
  alignItems: 'flex-start',
  paddingLeft: '10px',
  gap: '0.5rem',
  '& span': {
    display: 'inline-flex',
    flexShrink: 0,
    marginTop: '0.1em',
    '& svg': {
      width: '20px',
      height: '20px',
      display: 'block',
    },
  },
})

const buttonIconStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minWidth: '24px',
  minHeight: '24px',
  cursor: 'pointer',
  '& svg': {
    width: '20px',
    height: '20px',
    display: 'block',
  },
})

const copiedPopupStyle = css({
  position: 'absolute',
  top: '-40px',
  left: '50%',
  transform: 'translateX(-50%)',
  padding: '0.5rem 1rem',
  borderRadius: '4px',
  fontSize: '14px',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-5px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 0,
    borderLeft: '5px solid transparent',
    borderRight: '5px solid transparent',
    borderTop: '5px solid #333',
  },
})
